/**
 * Chat Image API Client
 *
 * API functions for conversational image generation.
 */

import apiClient from '../api-client';

export interface ChatConversation {
  id: string;
  workspace_id: string;
  user_id: string;
  title: string | null;
  model: string;
  settings: Record<string, string>;
  message_count: number;
  total_images: number;
  total_credits: number;
  status: string;
  retention_expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ChatImageRef {
  image_id: string;
  s3_key: string;
  url: string;
  mime_type: string;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  workspace_id: string;
  role: 'user' | 'model' | 'upload';
  text_content: string | null;
  image_refs: ChatImageRef[];
  token_usage: Record<string, number> | null;
  credits_charged: number;
  model: string | null;
  aspect_ratio: string | null;
  resolution: string | null;
  sequence_number: number;
  created_at: string;
}

export interface SendMessageResponse {
  success: boolean;
  conversation_id: string;
  model_message_id: string | null;
  text_content: string | null;
  images: ChatImageRef[];
  credits_charged: number;
  cached_tokens: number;
  error: string | null;
}

export async function createConversation(data: {
  title?: string;
  model?: string;
  settings?: Record<string, string>;
}): Promise<ChatConversation> {
  const res = await apiClient.post('/api/v2/chat-image/conversations', data);
  return res.data.conversation;
}

export async function listConversations(params?: {
  limit?: number;
  offset?: number;
}): Promise<ChatConversation[]> {
  const res = await apiClient.get('/api/v2/chat-image/conversations', { params });
  return res.data.conversations;
}

export async function getConversation(
  conversationId: string,
): Promise<ChatConversation & { messages: ChatMessage[] }> {
  const res = await apiClient.get(`/api/v2/chat-image/conversations/${conversationId}`);
  return res.data.conversation;
}

export async function deleteConversation(conversationId: string): Promise<void> {
  await apiClient.delete(`/api/v2/chat-image/conversations/${conversationId}`);
}

export async function sendMessage(
  conversationId: string,
  data: {
    text: string;
    image_ids?: string[];
    locale?: string;
    aspect_ratio?: string;
    resolution?: string;
  },
): Promise<SendMessageResponse> {
  const res = await apiClient.post(
    `/api/v2/chat-image/conversations/${conversationId}/messages`,
    data,
  );
  return res.data;
}

export async function renameConversation(
  conversationId: string,
  title: string,
): Promise<{ success: boolean; title: string }> {
  const res = await apiClient.patch(
    `/api/v2/chat-image/conversations/${conversationId}/title`,
    { title },
  );
  return res.data;
}

export async function saveImageToGallery(
  imageId: string,
  data: {
    conversation_id: string;
    message_id: string;
  },
): Promise<{ success: boolean; gallery_image_id: string }> {
  const res = await apiClient.post(
    `/api/v2/chat-image/images/${imageId}/save-to-gallery`,
    data,
  );
  return res.data;
}

export interface UploadImageResponse {
  success: boolean;
  image_id: string;
  url: string;
  mime_type: string;
  error?: string;
}

export async function uploadReferenceImage(
  conversationId: string,
  file: File,
): Promise<UploadImageResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiClient.post(
    `/api/v2/chat-image/conversations/${conversationId}/upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data;
}
